import { useAsync } from 'react-use'
import { useTranslation } from 'react-i18next'
import { Event } from '@nostr/tools/wasm'
import { fetchEvent } from '@/services/client.service'
import { SecondaryPageLink } from '@/PageManager'
import { toNote } from '@/lib/link'
import { cn } from '@/lib/utils'
import { generateBech32IdFromATag, generateBech32IdFromETag, tagNameEquals } from '@/lib/tag'
import Emoji from '../Emoji'
import { parseReactionEmoji } from '../ReactionList'
import ContentPreview from '../ContentPreview'
import { useMemo } from 'react'

export default function Reaction({ className, event }: { className?: string; event: Event }) {
  const { t } = useTranslation()
  const { value: targetEvent } = useAsync(async () => {
    const eTag = event.tags.find(tagNameEquals('e'))
    if (eTag?.[1]) {
      return fetchEvent({
        id: eTag[1],
        relays: eTag[2] ? [eTag[2]] : [],
        author: eTag[4]
      })
    }

    const aTag = event.tags.find(tagNameEquals('a'))
    if (aTag?.[1]) {
      const [kind, pubkey, ...identifier] = aTag[1].split(':')
      return fetchEvent({
        kind: Number(kind),
        pubkey,
        identifier: identifier.join(':'),
        relays: aTag[2] ? [aTag[2]] : []
      })
    }

    return undefined
  }, [event])

  const fallbackId = useMemo(() => {
    const eTag = event.tags.find(tagNameEquals('e'))
    if (eTag?.[1]) return generateBech32IdFromETag(eTag)
    const aTag = event.tags.find(tagNameEquals('a'))
    if (aTag?.[1]) return generateBech32IdFromATag(aTag)
    return undefined
  }, [event])

  const target = targetEvent ?? fallbackId
  const href = target ? toNote(target) : undefined

  return (
    <div className={cn('mt-2', className)}>
      <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-2">
        <Emoji emoji={parseReactionEmoji(event)} classNames={{ text: 'text-lg', img: 'size-4' }} />
        <span>{t('reacted')}</span>
      </div>
      {href && (
        <SecondaryPageLink
          to={href}
          onClick={(e) => e.stopPropagation()}
          className="block border rounded-lg p-3 bg-muted/30 hover:bg-muted/60 transition-colors"
        >
          {targetEvent ? (
            <ContentPreview event={targetEvent} />
          ) : (
            <span className="text-sm text-muted-foreground font-mono truncate block">
              {fallbackId}
            </span>
          )}
        </SecondaryPageLink>
      )}
    </div>
  )
}
